import { useCallback, useMemo } from "react";
import { Redirect, Route, Switch, useHistory, useParams } from "react-router-dom";

import { Container } from "@material-ui/core";
import moment from "moment";

import { TitleHeader } from "../../components/TitleHeader";
import { TabBar } from "../../components/tabs/TabBar";
import { TabBarItem } from "../../components/tabs/TabBarItem";
import { ContentSection } from "../../layout/ContentSection";
import { ListSection, ListSectionProps } from "../../components/admin-list-section/ListSection";
import { NotFoundPage } from "../../NotFoundPage";
import { useIsSuperAdministrator } from "../../LinkProvider";
import { useClarifyClassificationTasks } from "../../workflow/follow-up/appointment/TaskProvider";
import { ClarifyClassificationTaskDocument } from "../../task/task.model";

export const IntegratedClarifyClassificationOverviewPage = () => {
    const isSuperAdministrator = useIsSuperAdministrator();

    const { tab } = useParams<{ tab: "email-classification" }>();

    const tabIndex = useMemo(() => {
        switch ( tab ) {
            case "email-classification": return 0;

            default: return 0;
        }
    }, [tab]);

    const history = useHistory();
    const goToTask = useCallback((task: ClarifyClassificationTaskDocument) => {
        history.push(`/clarify-classification/email-classification/${task.id}`);
    }, [history]);

    const now = moment().toISOString(true);
    const tasks = useClarifyClassificationTasks();
    const currentTasks = useMemo(() => tasks.filter(task => task.performAfterDate <= now), [tasks, now]);
    const futureTasks = useMemo(() => tasks.filter(task => task.performAfterDate > now), [tasks, now]);

    const currentTaskListItems: ListSectionProps["items"] = useMemo(() => currentTasks.map(mapToListItem), [currentTasks]);
    const futureTaskListItems: ListSectionProps["items"] = useMemo(() => futureTasks.map(mapToListItem), [futureTasks]);

    const goToCurrentTask = useCallback((index: number) => goToTask(currentTasks[index]), [goToTask, currentTasks]);
    const goToFutureTask = useCallback((index: number) => goToTask(futureTasks[index]), [goToTask, futureTasks]);

    if ( !isSuperAdministrator ) return <NotFoundPage />;
    if ( tab !== "email-classification" ) return <Redirect to="/clarify-classification/email-classification" />;

    return (
        <>
            <TitleHeader>Afklaring</TitleHeader>

            <TabBar index={tabIndex}>
                <TabBarItem href="/clarify-classification/email-classification">{formatTitle("Klassificering af email", currentTasks.length)}</TabBarItem>
            </TabBar>

            <Switch>

                <Route exact path="/clarify-classification/email-classification">
                    <ContentSection>
                        <Container maxWidth="sm" disableGutters>

                            <ListSection
                                title="Aktuelle opgaver"
                                placeholder="Der er ingen aktuelle opgaver"
                                items={currentTaskListItems}
                                onClick={goToCurrentTask}
                            />

                            <ListSection
                                title="Fremtidige opgaver"
                                placeholder="Der er ingen fremtidige opgaver"
                                items={futureTaskListItems}
                                onClick={goToFutureTask}
                            />

                        </Container>
                    </ContentSection>
                </Route>

            </Switch>
        </>
    );
};

const mapToListItem = (task: ClarifyClassificationTaskDocument): ListSectionProps["items"][0] => {
    const formattedDate = moment(task.creationDate).format("D MMMM YYYY HH:mm");

    return {
        heading: task.heading,
        subheading: `Modtaget ${formattedDate}`,
    };
};

function formatTitle(text: string, count: number): string {
    if ( count === 0 ) return text;

    return `${text} (${count})`;
};
